import { createCsvMaker } from './csv';
import { transformCuts } from './data';
import { CutAPI, cutApi } from '@api/cut';

import type { Cut, ParsedCut } from '@/types/cuts';
import type { SortDirections } from '@/types/sort';
import type { SearchIDType } from '@consts/cuts';
import type { useOrgs } from '@hooks/useOrgs';

type SearchParams = {
    searchID: string;
    searchIDType: SearchIDType;
    pageNum: number;
    pageSize: number;
    countOnly?: 'true';
};

type ActivityParams = {
    sortDirection: SortDirections;
    pageNum: number;
    pageSize: number;
    startDate: string;
    endDate: string;
    searchID?: undefined;
    searchIDType?: undefined;
    countOnly?: 'true';
};

export type ExportActivityParams = SearchParams | ActivityParams;

/**
 * Whether or not the provided {@link ExportActivityParams} is {@link SearchParams},
 * which determines slightly different exporting behavior.
 */
export const isSearchParams = (params: ExportActivityParams): params is SearchParams => {
    return 'searchID' in params;
};

/**
 * Open the file picker for the user to choose a file, and created an
 * {@link OrderedWriter} to write to the opened file.
 *
 * **Note:** On browsers that don't support the save file picker yet,
 * the file will just be written to the downloads folder.
 */
export const getWriter = (params: ExportActivityParams) => {
    const fileName = isSearchParams(params)
        ? `Search-${params.searchIDType}-${params.searchID}`
        : `${params.startDate}-to-${params.endDate}`;

    return new WritableBlob(`${fileName}.csv`, 'text/csv');
};

export const getCutData =
    (token: string, { companies, locations, resellers }: ReturnType<typeof useOrgs>) =>
        (params: ExportActivityParams) =>
            async () => {
                const cuts = await cutApi<unknown, Cut[]>(
                    isSearchParams(params) ? CutAPI.Search : CutAPI.Activity,
                    'GET'
                )({ token, params });

                return transformCuts(cuts, companies, locations, resellers);
            };

export const cutDataCsvMaker = createCsvMaker<ParsedCut>({
    order: [
        ['cutDate', 'Cut Date'],
        ['syncDate', 'Sync Date'],
        ['cutId', 'Cut ID'],
        ['batchId', 'Batch ID'],
        ['creditId', 'Credit ID'],
        ['cutType', 'Type'],
        ['material', 'Material'],
        ['company', 'Company'],
        ['reseller', 'Reseller'],
        ['location', 'Location'],
        ['locationId', 'Location ID'],
        ['manufacturer', 'Manufacturer'],
        ['model', 'Model'],
        ['coverage', 'Coverage'],
        ['cutStatus', 'Status'],
        ['country', 'Country'],
        ['employeeEmail', 'Employee Email'],
        ['category', 'Category'],
    ],
});

export class WritableBlob {
    #body = document.querySelector('body')!;
    #link = document.createElement('a');
    #parts: string[] = [];

    constructor(public fileName: string, private mimeType: string) {
        // Don't display the element on the page
        this.#link.setAttribute('hidden', 'hidden');
        this.#link.setAttribute('download', fileName);
        this.#link.setAttribute('data-testid', 'download-link');

        this.#body.prepend(this.#link);
    }

    write(data: string) {
        // Push the data to the parts from which the blob will be created
        this.#parts.push(data);
    }

    close() {
        // Compile the data into a blob.
        const blob = new Blob(this.#parts, { type: this.mimeType });
        // Cleanup - allow for early garbage collection
        this.#parts = [];

        // Create the link for the file
        const url = URL.createObjectURL(blob);
        this.#link.setAttribute('href', url);

        // Automatically click the download link
        this.#link.click();
    }
}
